import React from "react";
import tw from "twin.macro";
import { SectionHeading, Subheading } from "components/misc/Headings.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import ProductFrame from "components/misc/ProductFrame.js";
import reportImage from "images/product/anonymous-report.jpg";

/*
 * EthosHub's main differentiator, stated plainly.
 *
 * Every claim below is verified against the application code (see the
 * internal reference doc, section 5). Keep it that way: if the implementation
 * changes, this copy must change with it. In particular, "never reads IP
 * addresses" is a claim about the application - infrastructure access logs
 * are a separate question.
 */
const Section = tw.div`bg-gray-100 -mx-8 px-8`;
const Row = tw.div`flex flex-col lg:flex-row gap-12 lg:gap-16 items-center max-w-screen-xl mx-auto`;
const TextColumn = tw.div`w-full lg:w-1/2`;
const ImageColumn = tw.div`w-full lg:w-1/2`;
const Eyebrow = tw(Subheading)`text-left`;
const Heading = tw(SectionHeading)`text-left mt-2`;
const Intro = tw.p`mt-4 text-lg leading-relaxed text-gray-800`;
const Points = tw.dl`mt-10 space-y-8`;
const Point = tw.div`pl-5 border-l-4 border-primary-500`;
const Term = tw.dt`font-bold text-lg text-gray-900`;
const Detail = tw.dd`mt-1 text-gray-800 leading-relaxed`;
const Note = tw.p`mt-10 text-sm text-gray-700 leading-relaxed`;

const points = [
  {
    term: "No IP address, no device fingerprint",
    detail: "The EthosHub application never reads, logs or stores a reporter's IP address, browser details or device information.",
  },
  {
    term: "No account and no cookies",
    detail: "Anonymous reporters don't sign in, and no session or tracking cookie is created when they submit.",
  },
  {
    term: "A key only the reporter holds",
    detail: "Each report generates a random one-time access key, shown once. We store only a one-way hash of it — so nobody, including EthosHub, can trace a report back to the person who made it.",
  },
  {
    term: "Hidden in an instant",
    detail: "If someone walks past, a discreet Hide button — or Esc pressed twice — covers the report with an ordinary weather forecast, even renaming the browser tab. Nothing typed is lost, and nothing is sent anywhere.",
  },
  {
    term: "Follow-up without identification",
    detail: "Caseworkers can ask questions and the reporter can reply using their key. The caseworker only ever sees “Anonymous”, and leadership dashboards never retrieve reporter identity at all.",
  },
];

export default () => (
  <Section>
    <Container>
      <ContentWithPaddingXl>
        <Row>
          <TextColumn>
            <Eyebrow>Anonymous reporting</Eyebrow>
            <Heading>How anonymity actually works</Heading>
            <Intro>
              Most platforms promise anonymity. Here is exactly what ours does — and doesn't — keep.
            </Intro>
            <Points>
              {points.map(p => (
                <Point key={p.term}>
                  <Term>{p.term}</Term>
                  <Detail>{p.detail}</Detail>
                </Point>
              ))}
            </Points>
            <Note>
              The trade-off: because the key is never stored, a reporter who loses it cannot
              recover it. We think genuine anonymity is worth that.
            </Note>
          </TextColumn>
          <ImageColumn>
            <ProductFrame
              src={reportImage}
              url="app.ethoshub.io/report"
              alt="The EthosHub anonymous report form, explaining that the report will be submitted anonymously and that a one-time access token will be shown once."
            />
          </ImageColumn>
        </Row>
      </ContentWithPaddingXl>
    </Container>
  </Section>
);
